import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CompetenceMonthField from './CompetenceMonthField';
import { fetchInvoiceEvents, fetchMyObligation } from '../utils/monthlyInvoiceApi';
import {
  buildOwnSummary,
  currentCompetenceBrazil,
  HistoryTarget,
  monthlyInvoiceErrorMessage,
  NOT_OBLIGATED_MESSAGE,
  ObligationRow,
  OwnSummary,
} from '../utils/monthlyInvoiceView';

interface MyInvoiceObligationSummaryProps {
  viewerId: string;
  loadMine?: (competence: string) => Promise<ObligationRow>;
  onOpenHistory?: (target: HistoryTarget) => void;
}

const MyInvoiceObligationSummary: React.FC<MyInvoiceObligationSummaryProps> = ({
  viewerId,
  loadMine = fetchMyObligation,
  onOpenHistory,
}) => {
  const [competence, setCompetence] = useState(currentCompetenceBrazil);
  const [summary, setSummary] = useState<OwnSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventsNote, setEventsNote] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setEventsNote(null);
    void loadMine(competence)
      .then((row) => {
        if (!active) return;
        const next = buildOwnSummary(row, viewerId);
        if ('blocked' in next) {
          setSummary(null);
          setError('Não foi possível mostrar esta situação.');
          return;
        }
        setSummary(next);
      })
      .catch((cause) => {
        if (!active) return;
        setSummary(null);
        setError(monthlyInvoiceErrorMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [competence, loadMine, viewerId]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 800 }}>
        Sua competência
      </Typography>
      <Box sx={{ mt: 1, mb: 1 }}>
        <CompetenceMonthField
          label="Competência"
          value={competence}
          onChange={setCompetence}
          minWidth={240}
        />
      </Box>
      {loading ? <Typography color="text.secondary">Carregando sua situação…</Typography> : null}
      {error ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
      {summary ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Typography variant="body2">Competência {summary.competence}</Typography>
          {summary.headline === NOT_OBLIGATED_MESSAGE ? (
            <Alert severity="info">{summary.headline}</Alert>
          ) : (
            <Typography variant="body2">{summary.headline}</Typography>
          )}
          {summary.documentary ? (
            <Typography variant="body2">{summary.documentary}</Typography>
          ) : null}
          {summary.total != null && summary.headline !== NOT_OBLIGATED_MESSAGE ? (
            <Typography variant="body2">{summary.total} nota(s)</Typography>
          ) : null}
          {summary.multipleLabel ? <Typography variant="body2">{summary.multipleLabel}</Typography> : null}
          {summary.countsLabel ? (
            <Typography variant="caption" color="text.secondary">
              {summary.countsLabel}
            </Typography>
          ) : null}
          {summary.notes.map((note) => (
            <Stack key={note.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                {note.number} · {note.status}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  onOpenHistory?.({ ...summary.history, invoiceId: note.id });
                  void fetchInvoiceEvents(note.id, 'mine')
                    .then((events) => {
                      setEventsNote(
                        events.length
                          ? `${note.number}: ${events.length} evento(s) no histórico existente.`
                          : `${note.number}: nenhum evento registrado.`,
                      );
                    })
                    .catch(() => setEventsNote('Não foi possível carregar os eventos desta nota.'));
                }}
              >
                Eventos
              </Button>
            </Stack>
          ))}
          <Button size="small" href={`#${summary.history.sectionId}`} onClick={() => onOpenHistory?.(summary.history)}>
            Abrir meu histórico
          </Button>
          {eventsNote ? (
            <Typography variant="body2" color="text.secondary">
              {eventsNote}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default MyInvoiceObligationSummary;
